// src/pages/AuthActionPage.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./AuthActionPage.css";

import {
  getSafeContinueUrl,
  executeAuthAction,
  submitPasswordReset,
  getFriendlyAuthActionError,
} from "../services/authActionDataService";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function AuthActionPage() {
  const navigate = useNavigate();
  const query = useQuery();

  const mode = query.get("mode") || "";
  const oobCode = query.get("oobCode") || "";
  const continueUrlRaw = query.get("continueUrl") || "";
  const continueUrl = getSafeContinueUrl(continueUrlRaw);

  const [status, setStatus] = useState("loading");
  const [title, setTitle] = useState("Einen Moment…");
  const [message, setMessage] = useState("Wir prüfen deinen Link.");
  const [error, setError] = useState("");

  const [newPassword, setNewPassword] = useState("");
  const [resetReady, setResetReady] = useState(false);

  const goToApp = () => {
    if (continueUrl) {
      navigate(continueUrl);
      return;
    }
    navigate("/login");
  };

  useEffect(() => {
    let isMounted = true;

    const run = async () => {
      try {
        const result = await executeAuthAction({ mode, oobCode });

        if (!isMounted) return;

        if (result.type === "verifyEmail") {
          setStatus("success");
          setTitle(result.title);
          setMessage(result.message);
          setError("");

          setTimeout(() => {
            if (continueUrl) navigate(continueUrl);
            else navigate("/login");
          }, 900);

          return;
        }

        if (result.type === "resetPassword") {
          setStatus("success");
          setTitle(result.title);
          setMessage(result.message);
          setResetReady(true);
          setError("");
          return;
        }

        setStatus("error");
        setTitle(result.title || "❌ Konnte nicht abgeschlossen werden");
        setMessage(result.message || "Unbekannte Aktion.");
        setError(result.type || "unsupported");
      } catch (err) {
        const mapped = getFriendlyAuthActionError(err);

        if (!isMounted) return;

        setStatus("error");
        setTitle("❌ Konnte nicht abgeschlossen werden");
        setMessage(mapped.friendly);
        setError(mapped.code || mapped.raw || "unknown-error");
      }
    };

    run();

    return () => {
      isMounted = false;
    };
  }, [mode, oobCode, continueUrl, navigate]);

  const handleResetSubmit = async () => {
  if (!resetReady) return;

  if (!newPassword.trim()) {
    setError("Bitte gib ein neues Passwort ein.");
    return;
  }

  try {
      const result = await submitPasswordReset({ oobCode, newPassword });

      setTitle(result.title);
      setMessage(result.message);
      setError("");
      setResetReady(false);
    } catch (err) {
      const mapped = getFriendlyAuthActionError(err);
      setError(mapped.code || mapped.raw || "Fehler beim Zurücksetzen.");
    }
  };

  return (
    <div className="auth-action-page">
      <img
        src="/logo-edens-ernte-transparent.png"
        alt="Edens Ernte"
        className="auth-action-logo"
        onClick={() => navigate("/")}
      />

      <div className="auth-action-box">
        <h2 className="auth-action-title">{title}</h2>

        <p className="auth-action-text">{message}</p>

        {mode === "resetPassword" && status !== "error" && resetReady && (
          <div className="auth-action-reset">
            <input
              className="auth-action-input"
              type="password"
              placeholder="Neues Passwort"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <button className="auth-action-button green" onClick={handleResetSubmit}>
              Passwort speichern
            </button>
          </div>
        )}

        {status === "loading" && <div className="auth-action-spinner" />}

        {status !== "loading" && (
  <button className="auth-action-button green" onClick={goToApp}>
    {mode === "resetPassword" ? "Zum Login" : "Zur App"}
  </button>
)}

        {status === "error" && (
          <details className="auth-action-details">
            <summary>Details anzeigen</summary>
            <div className="auth-action-error">{error}</div>
          </details>
        )}
      </div>
    </div>
  );
}